import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdRowActionIcon } from './mdPageIcons';

function ActionContent({ item }) {
  return (
    <>
      {item.icon ? <MdRowActionIcon name={item.icon} /> : null}
      {item.label}
    </>
  );
}

function MenuAction({ item, onSelect }) {
  const className = 'md-page__actions-menu-item md-page__actions-menu-item--' + (item.variant || 'outline');

  if (item.to) {
    return (
      <Link className={className} to={item.to} role="menuitem" onClick={onSelect}>
        <ActionContent item={item} />
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      role="menuitem"
      onClick={() => {
        onSelect();
        if (item.onClick) {
          item.onClick();
        }
      }}
      disabled={item.disabled}
    >
      <ActionContent item={item} />
    </button>
  );
}

const MENU_MIN_WIDTH = 148;
const MENU_ITEM_HEIGHT = 36;

function computeMenuPosition(buttonEl, menuEl, actionCount) {
  const buttonRect = buttonEl.getBoundingClientRect();
  const menuWidth = menuEl ? menuEl.offsetWidth : MENU_MIN_WIDTH;
  const menuHeight = menuEl ? menuEl.offsetHeight : actionCount * MENU_ITEM_HEIGHT + 12;

  let top = buttonRect.bottom + 4;
  if (top + menuHeight > window.innerHeight - 8) {
    top = Math.max(8, buttonRect.top - menuHeight - 4);
  }

  let left = buttonRect.right - menuWidth;
  left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));

  return { top, left };
}

/**
 * Todas las acciones en menú ⋮ para dejar la columna mínima y maximizar datos visibles.
 * Orden: Modificar, Borrar, Detalles, …
 */
export function MdRowActions({ items }) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const actions = items || [];

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setMenuPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!buttonRef.current) {
        return;
      }
      setMenuPosition(computeMenuPosition(buttonRef.current, menuRef.current, actions.length));
    };

    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, actions.length]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const closeMenu = () => setOpen(false);

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className="md-page__actions md-page__actions--menu-only" ref={rootRef}>
      <div className="md-page__actions-overflow">
        <button
          ref={buttonRef}
          type="button"
          className="md-page__btn md-page__btn--menu"
          aria-label="Acciones"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          ⋮
        </button>
        {open ? (
          <div
            ref={menuRef}
            className="md-page__actions-menu md-page__actions-menu--fixed"
            style={
              menuPosition
                ? { top: menuPosition.top, left: menuPosition.left, visibility: 'visible' }
                : { visibility: 'hidden' }
            }
            role="menu"
          >
            {actions.map((item) => (
              <MenuAction key={item.key} item={item} onSelect={closeMenu} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function buildStandardRowActions({
  editTo,
  onDelete,
  deleteDisabled,
  deleteLabel,
  detailTo,
  extraActions = [],
}) {
  const actions = [
    {
      key: 'edit',
      label: 'Modificar',
      variant: 'primary',
      icon: 'pencil',
      to: editTo,
    },
    {
      key: 'delete',
      label: deleteLabel || 'Borrar',
      variant: 'danger',
      icon: 'trash',
      onClick: onDelete,
      disabled: deleteDisabled,
    },
  ];

  if (detailTo) {
    actions.push({
      key: 'detail',
      label: 'Detalles',
      variant: 'outline',
      icon: 'info',
      to: detailTo,
    });
  }

  return actions.concat(extraActions);
}

export function mdTableCellTypeClass(cellType) {
  if (!cellType) return '';
  return `md-page__table__cell--${cellType}`;
}

export function MdTableCell({ children, cellType, className = '' }) {
  const title = children != null && children !== '' ? String(children) : undefined;
  const classes = [mdTableCellTypeClass(cellType), className].filter(Boolean).join(' ');
  return <td className={classes || undefined} title={title}>{children}</td>;
}

export function MdTableHeaderCell({ children, cellType, className = '', ...rest }) {
  const classes = [mdTableCellTypeClass(cellType), className].filter(Boolean).join(' ');
  return (
    <th scope="col" className={classes || undefined} {...rest}>
      {children}
    </th>
  );
}

export function MdTableActionsCell({ children }) {
  return <td className="md-page__table__actions">{children}</td>;
}
